
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import Navbar from "../components/Homepage/Navbar/Navbar";
import Footer from "../components/Homepage/Footer/Footer";

const API_URL = "https://rukap.edu.in/leave-api";
// const API_URL = "http://127.0.0.1:5000";
 

export default function AllEmployeeLeaves() {
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("leaveUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const role = String(user.role || "").toUpperCase();
  const isRegistrarOrAdmin = ["REGISTRAR", "ADMIN"].includes(role);

  useEffect(() => {
    let active = true;

    const loadApplications = async () => {
      const token = sessionStorage.getItem("leaveToken");

      if (!token) {
        navigate("/leave-portal/login", { replace: true });
        return;
      }

      if (!["HOD", "PRINCIPAL", "REGISTRAR", "ADMIN"].includes(role)) {
        navigate("/leave-portal", { replace: true });
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await axios.get(
          `${API_URL}/api/admin/leaves`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!active) return;

        const rows = response.data.applications || [];

        setApplications(
          rows.map((application) => {
            let dates = application.dates;

            if (!Array.isArray(dates)) {
              dates = application.leave_dates
                ? application.leave_dates.split(",")
                : [];
            }

            return {
              ...application,
              dates: dates
                .map((date) => String(date).trim())
                .filter(Boolean),
            };
          })
        );
      } catch (err) {
        if (!active) return;

        if (err.response?.status === 401) {
          setError("Your session has expired. Please log in again.");
        } else if (err.response?.status === 403) {
          setError("You do not have permission to view these leave records.");
        } else {
          setError(
            err.response?.data?.message ||
              "Unable to load employee leave records."
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadApplications();

    return () => {
      active = false;
    };
  }, [navigate, role]);

  const formatDate = (value) => {
    const match = String(value || "")
      .slice(0, 10)
      .match(/^(\d{4})-(\d{2})-(\d{2})$/);

    return match
      ? `${match[3]}-${match[2]}-${match[1]}`
      : String(value || "-");
  };

  const filteredApplications = useMemo(() => {
    const query = search.trim().toLowerCase();

    return applications.filter((application) => {
      const matchesStatus =
        statusFilter === "ALL" ||
        String(application.status || "").toUpperCase() === statusFilter;

      const searchableValues = [
        application.application_no,
        application.employee_name,
        application.employee_code,
        application.employee_type,
        application.designation,
        application.department_name,
        application.college_name,
        application.leave_type,
        application.purpose,
        application.status,
        ...(application.dates || []),
      ];

      const matchesSearch =
        !query ||
        searchableValues.some((value) =>
          String(value || "").toLowerCase().includes(query)
        );

      return matchesStatus && matchesSearch;
    });
  }, [applications, search, statusFilter]);

  const downloadPDF = () => {
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("RAYALASEEMA UNIVERSITY", 148.5, 14, {
        align: "center",
      });

      doc.setFontSize(12);
      doc.text("All Employee Leave Records", 148.5, 22, {
        align: "center",
      });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(`Report Role: ${role}`, 10, 30);
      doc.text(`Total Records: ${filteredApplications.length}`, 10, 36);

      const rows = filteredApplications.map((application, index) => [
        index + 1,
        application.application_no || "-",
        application.employee_code || "-",
        application.employee_name || "-",
        application.employee_type || "-",
        application.designation || "-",
        application.department_name || "-",
        application.college_name || "-",
        application.leave_type || "-",
        application.total_days ?? 0,
        (application.dates || []).map(formatDate).join(", ") || "-",
        application.purpose || "-",
        application.status || "-",
      ]);

      autoTable(doc, {
        startY: 41,
        head: [[
          "S.No.",
          "Application No.",
          "Employee Code",
          "Employee Name",
          "Type",
          "Designation",
          "Department",
          "College",
          "Leave Type",
          "Days",
          "Leave Dates",
          "Purpose",
          "Status",
        ]],
        body:
          rows.length > 0
            ? rows
            : [["-", "No leave records found", "", "", "", "", "", "", "", "", "", "", ""]],
        theme: "grid",
        styles: {
          fontSize: 6.5,
          cellPadding: 1.8,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: [11, 60, 93],
          textColor: 255,
        },
        margin: { left: 8, right: 8, bottom: 12 },
        didDrawPage: () => {
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();

          doc.setFontSize(8);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
            pageWidth - 10,
            pageHeight - 5,
            { align: "right" }
          );
        },
      });

      doc.save("Rayalaseema_University_All_Employee_Leaves.pdf");
    } catch (err) {
      console.error("Leave report PDF error:", err);
      window.alert("Unable to generate PDF. Check the browser console.");
    }
  };

  return (
    <>
      <Navbar />

      <Container maxWidth="xl" sx={{ py: 4, minHeight: "65vh" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} color="#0B3C5D">
              All Employee Leave Records
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Rayalaseema University Leave Management Portal
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/leave-portal/admin-dashboard")}
            >
              Back to Dashboard
            </Button>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadPDF}
              sx={{
                bgcolor: "#0B3C5D",
                "&:hover": { bgcolor: "#082F49" },
              }}
            >
              Download PDF
            </Button>
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: "1px solid #dce3ea",
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search employee, department, application number, purpose..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              size="small"
              label="Application Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: { xs: "100%", md: 190 } }}
            >
              <option value="ALL">All Statuses</option>
              <option value="APPROVED">Approved</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </TextField>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Records displayed: {filteredApplications.length} of{" "}
            {applications.length}
            {!isRegistrarOrAdmin &&
              " | Results are limited to your authorized department or college."}
          </Typography>
        </Paper>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              error.includes("session has expired") ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    navigate("/leave-portal/login", { replace: true })
                  }
                >
                  Login
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !error ? (
          <TableContainer
            component={Paper}
            sx={{
              border: "1px solid #dce3ea",
              borderRadius: 2,
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#0B3C5D" }}>
                  {[
                    "Application No.",
                    "Employee Code",
                    "Employee Name",
                    "Type",
                    "Designation",
                    "Department",
                    "College",
                    "Leave Type",
                    "Days",
                    "Leave Dates",
                    "Purpose",
                    "Status",
                    "Submitted On",
                  ].map((heading) => (
                    <TableCell
                      key={heading}
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredApplications.map((application) => (
                  <TableRow
                    hover
                    key={
                      application.leave_id ||
                      application.application_no
                    }
                  >
                    <TableCell>
                      {application.application_no || "-"}
                    </TableCell>
                    <TableCell>
                      {application.employee_code || "-"}
                    </TableCell>
                    <TableCell>
                      {application.employee_name || "-"}
                    </TableCell>
                    <TableCell>
                      {String(application.employee_type || "-").replace(
                        "_",
                        " "
                      )}
                    </TableCell>
                    <TableCell>{application.designation || "-"}</TableCell>
                    <TableCell>
                      {application.department_name || "-"}
                    </TableCell>
                    <TableCell>{application.college_name || "-"}</TableCell>
                    <TableCell>{application.leave_type || "-"}</TableCell>
                    <TableCell>{application.total_days ?? 0}</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      {(application.dates || [])
                        .map(formatDate)
                        .join(", ") || "-"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      {application.purpose || "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={application.status || "UNKNOWN"}
                        color={
                          String(application.status).toUpperCase() ===
                          "APPROVED"
                            ? "success"
                            : String(application.status).toUpperCase() ===
                                "REJECTED"
                              ? "error"
                              : "warning"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {application.submitted_at
                        ? new Date(
                            application.submitted_at
                          ).toLocaleDateString("en-IN", {
                            timeZone: "Asia/Kolkata",
                          })
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}

                {filteredApplications.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      No leave applications found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Container>

      <Footer />
    </>
  );
}
